import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AppLayout } from "./components/layout/AppLayout";
import { PageErrorBoundary } from "./components/PageErrorBoundary";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { AdminLayout } from "./features/admin/AdminLayout";

const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const LandingPage = lazy(() =>
  import("./pages/LandingPage").then((m) => ({ default: m.LandingPage })),
);
const LoginRedirectPage = lazy(() =>
  import("./pages/LoginRedirectPage").then((m) => ({
    default: m.LoginRedirectPage,
  })),
);
const OnboardingPage = lazy(() =>
  import("./pages/OnboardingPage").then((m) => ({
    default: m.OnboardingPage,
  })),
);
const FlowPipelinesPage = lazy(() =>
  import("./pages/flow/FlowPipelinesPage").then((m) => ({
    default: m.FlowPipelinesPage,
  })),
);
const FlowDashboardPage = lazy(() =>
  import("./pages/flow/FlowDashboardPage").then((m) => ({
    default: m.FlowDashboardPage,
  })),
);
const FormsListPage = lazy(() =>
  import("./pages/forms/FormsListPage").then((m) => ({
    default: m.FormsListPage,
  })),
);
const FormEditor = lazy(() =>
  import("./features/forms/FormEditor").then((m) => ({
    default: m.FormEditor,
  })),
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const PublicFormPage = lazy(() =>
  import("./pages/forms/PublicFormPage").then((m) => ({
    default: m.PublicFormPage,
  })),
);
const ActionTablesPage = lazy(() =>
  import("./pages/action-tables/ActionTablesPage").then((m) => ({
    default: m.ActionTablesPage,
  })),
);
const ActionTableViewerPage = lazy(() =>
  import("./pages/action-tables/ActionTableViewerPage").then((m) => ({
    default: m.ActionTableViewerPage,
  })),
);
const ActionTableEditorPage = lazy(() =>
  import("./pages/action-tables/ActionTableEditorPage").then((m) => ({
    default: m.ActionTableEditorPage,
  })),
);
const TeamsPage = lazy(() =>
  import("./pages/teams/TeamsPage").then((m) => ({
    default: m.TeamsPage,
  })),
);
const TeamDetailPage = lazy(() =>
  import("./pages/teams/TeamDetailPage").then((m) => ({
    default: m.TeamDetailPage,
  })),
);
const BillingPage = lazy(() =>
  import("./pages/billing/BillingPage").then((m) => ({
    default: m.BillingPage,
  })),
);
const AdminLoginPage = lazy(() =>
  import("./pages/admin/AdminLoginPage").then((m) => ({
    default: m.AdminLoginPage,
  })),
);
const AdminDashboardPage = lazy(() =>
  import("./pages/admin/AdminDashboardPage").then((m) => ({
    default: m.AdminDashboardPage,
  })),
);
const AdminTenantsPage = lazy(() =>
  import("./pages/admin/AdminTenantsPage").then((m) => ({
    default: m.AdminTenantsPage,
  })),
);
const AdminTenantDetailPage = lazy(() =>
  import("./pages/admin/AdminTenantDetailPage").then((m) => ({
    default: m.AdminTenantDetailPage,
  })),
);
const AdminHealthPage = lazy(() =>
  import("./pages/admin/AdminHealthPage").then((m) => ({
    default: m.AdminHealthPage,
  })),
);
const AdminActivationPage = lazy(() =>
  import("./pages/admin/AdminActivationPage").then((m) => ({
    default: m.AdminActivationPage,
  })),
);
const AdminBillingDashboardPage = lazy(() =>
  import("./pages/admin/billing/AdminBillingDashboardPage").then((m) => ({
    default: m.AdminBillingDashboardPage,
  })),
);
const AdminProductsPage = lazy(() =>
  import("./pages/admin/billing/AdminProductsPage").then((m) => ({
    default: m.AdminProductsPage,
  })),
);
const AdminPricingPage = lazy(() =>
  import("./pages/admin/billing/AdminPricingPage").then((m) => ({
    default: m.AdminPricingPage,
  })),
);
const AdminSubscriptionsPage = lazy(() =>
  import("./pages/admin/billing/AdminSubscriptionsPage").then((m) => ({
    default: m.AdminSubscriptionsPage,
  })),
);
const AdminTrialsPage = lazy(() =>
  import("./pages/admin/billing/AdminTrialsPage").then((m) => ({
    default: m.AdminTrialsPage,
  })),
);
const TemplatesPage = lazy(() =>
  import("./pages/templates/TemplatesPage").then((m) => ({
    default: m.TemplatesPage,
  })),
);
const TemplateDetailPage = lazy(() =>
  import("./pages/templates/TemplateDetailPage").then((m) => ({
    default: m.TemplateDetailPage,
  })),
);
const ProvisionPage = lazy(() =>
  import("./pages/templates/ProvisionPage").then((m) => ({
    default: m.ProvisionPage,
  })),
);

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingSpinner label="Loading page" size="lg" />}>
        <Routes>
          <Route
            index
            element={
              <PageErrorBoundary>
                <LandingPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="login"
            element={
              <PageErrorBoundary>
                <LoginRedirectPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="onboarding"
            element={
              <PageErrorBoundary>
                <OnboardingPage />
              </PageErrorBoundary>
            }
          />

          {/* Public form routes — no app shell or sidebar.
              /forms/:formId/view is the Action Tables launch URL with row/table context. */}
          <Route
            path="forms/public/:formId"
            element={
              <PageErrorBoundary>
                <PublicFormPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="forms/:formId/view"
            element={
              <PageErrorBoundary>
                <PublicFormPage />
              </PageErrorBoundary>
            }
          />

          <Route element={<AppLayout />}>
            <Route
              path="dashboard"
              element={
                <PageErrorBoundary>
                  <DashboardPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="flow"
              element={
                <PageErrorBoundary>
                  <FlowPipelinesPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="flow/dashboard"
              element={
                <PageErrorBoundary>
                  <FlowDashboardPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="forms"
              element={
                <PageErrorBoundary>
                  <FormsListPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="forms/:formId/edit"
              element={
                <PageErrorBoundary>
                  <FormEditor />
                </PageErrorBoundary>
              }
            />
            <Route
              path="action-tables"
              element={
                <PageErrorBoundary>
                  <ActionTablesPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="action-tables/new"
              element={
                <PageErrorBoundary>
                  <ActionTableEditorPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="action-tables/:tableId"
              element={
                <PageErrorBoundary>
                  <ActionTableViewerPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="action-tables/:tableId/edit"
              element={
                <PageErrorBoundary>
                  <ActionTableEditorPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="settings"
              element={
                <PageErrorBoundary>
                  <SettingsPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="teams"
              element={
                <PageErrorBoundary>
                  <TeamsPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="teams/:teamId"
              element={
                <PageErrorBoundary>
                  <TeamDetailPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="billing"
              element={
                <PageErrorBoundary>
                  <BillingPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="templates"
              element={
                <PageErrorBoundary>
                  <TemplatesPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="templates/:templateId"
              element={
                <PageErrorBoundary>
                  <TemplateDetailPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="templates/provisions/:provisionId"
              element={
                <PageErrorBoundary>
                  <ProvisionPage />
                </PageErrorBoundary>
              }
            />
          </Route>

          {/* Admin routes — separate layout, no app shell */}
          <Route
            path="admin/login"
            element={
              <PageErrorBoundary>
                <AdminLoginPage />
              </PageErrorBoundary>
            }
          />
          <Route path="admin" element={<AdminLayout />}>
            <Route
              index
              element={
                <PageErrorBoundary>
                  <AdminDashboardPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="tenants"
              element={
                <PageErrorBoundary>
                  <AdminTenantsPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="tenants/:tenantId"
              element={
                <PageErrorBoundary>
                  <AdminTenantDetailPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="health"
              element={
                <PageErrorBoundary>
                  <AdminHealthPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="activation"
              element={
                <PageErrorBoundary>
                  <AdminActivationPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="billing"
              element={
                <PageErrorBoundary>
                  <AdminBillingDashboardPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="billing/products"
              element={
                <PageErrorBoundary>
                  <AdminProductsPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="billing/pricing"
              element={
                <PageErrorBoundary>
                  <AdminPricingPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="billing/subscriptions"
              element={
                <PageErrorBoundary>
                  <AdminSubscriptionsPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="billing/trials"
              element={
                <PageErrorBoundary>
                  <AdminTrialsPage />
                </PageErrorBoundary>
              }
            />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
